import React from "react";
import Rows from "../Rows";
import AddressSelect from "./Select";
import { onFormItemDiff } from "./utils";
import { Form, Input, Select } from "antd";
import useAddressData from "./useAddressData";

import type { TypeAddressSelectProps } from "./Select";
import type { NamePath } from "rc-field-form/lib/interface";
import type { DefaultOptionType } from "rc-select/lib/Select";
import type { FormItemLabelProps } from "antd/es/form/FormItemLabel";

export interface TypeAddressSelectParma<T = DefaultOptionType> {
  /**
   * @param state 州
   */
  state: T[];
  /**
   * @param county 县
   */
  county: Record<string, T[]>;
}

export interface TypeAddressProps
  extends Pick<TypeAddressSelectProps, "fullAddress">,
    Pick<FormItemLabelProps, "label"> {
  /**
   * @param required 是否必填
   */
  required?: boolean;
  /**
   * @param disabled 是否可填
   */
  disabled?: boolean;
  /**
   * @param allowClear 是否开启清空图标
   */
  allowClear?: boolean;
  /**
   * @name onAddressChange Address状态发生变化时触发
   */
  onAddressChange?(): void;
  /**
   * @param fieldNames 可指定各类“地址字段”
   * @description "street" | "city" | "zip" | "state" | "county"
   */
  fieldNames?: Partial<
    Record<"street" | "city" | "zip" | "state" | "county" | "unit", NamePath>
  >;
}

/**
 * @name FIELD_NAMES_DEFAULT 默认配置字段
 */
const FIELD_NAMES_DEFAULT = {
  zip: "zip",
  city: "city",
  unit: "unit",
  state: "state",
  county: "county",
  street: "street",
};

/**
 * @name Address 地址Form组件
 * @description 包含 address、state、county、city、zip
 */
const Address: React.FC<TypeAddressProps> = ({
  disabled,
  required,
  allowClear,
  fullAddress,
  onAddressChange,
  label = "Address",
  fieldNames = FIELD_NAMES_DEFAULT,
}) => {
  const address = useAddressData();

  const form = Form.useFormInstance();

  // 自动关联设置其他地址信息
  function onRelation(
    address: Parameters<TypeAddressSelectProps["onAddress"]>[number],
  ) {
    form.setFields([
      { name: fieldNames.zip, value: address?.zip },
      { name: fieldNames.city, value: address?.city },
      { name: fieldNames.state, value: address?.state },
      { name: fieldNames.county, value: address?.county },
    ]);
    form.validateFields(Object.values(fieldNames));
  }

  // 手动触发“州”值变化，清空“县”
  function onStateChange() {
    form.setFields([{ name: fieldNames.county, value: undefined }]);
    onChange();
  }

  // Address 组件内字段变化触发
  function onChange() {
    onAddressChange?.();
  }

  return (
    <Rows span={6}>
      <Form.Item
        label={label}
        name={fieldNames.street}
        labelCol={{ span: fieldNames.unit ? 18 : 24 }}
        rules={[{ required, message: "address must not be empty" }]}
      >
        <AddressSelect
          disabled={disabled}
          onChange={onChange}
          onAddress={onRelation}
          fullAddress={fullAddress}
        />
      </Form.Item>

      {fieldNames.unit ? (
        <Form.Item label="Unit" name={fieldNames.unit}>
          <Input
            placeholder="Unit"
            disabled={disabled}
            onChange={onChange}
            allowClear={allowClear}
          />
        </Form.Item>
      ) : null}

      <Form.Item
        label="State"
        name={fieldNames.state}
        rules={[{ required, message: "must not be empty" }]}
      >
        <Select
          showSearch
          disabled={disabled}
          placeholder="State"
          allowClear={allowClear}
          optionFilterProp="label"
          onChange={onStateChange}
          loading={address?.loading}
          options={address?.data?.state}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(p, c) => onFormItemDiff(p, c, fieldNames.state)}
      >
        {(form) => {
          const state = form.getFieldValue(fieldNames.state);
          return (
            <Form.Item
              label="County"
              name={fieldNames.county}
              rules={[{ required, message: "must not be empty" }]}
            >
              <Select
                showSearch
                disabled={disabled}
                onChange={onChange}
                placeholder="County"
                allowClear={allowClear}
                optionFilterProp="label"
                loading={address?.loading}
                options={address?.data?.county?.[state]}
              />
            </Form.Item>
          );
        }}
      </Form.Item>

      <Form.Item
        label="City"
        name={fieldNames.city}
        rules={[{ required, message: "must not be empty" }]}
      >
        <Input
          placeholder="City"
          disabled={disabled}
          onChange={onChange}
          allowClear={allowClear}
        />
      </Form.Item>

      <Form.Item
        label="Zip"
        name={fieldNames.zip}
        rules={[{ required, message: "must not be empty" }]}
      >
        <Input
          placeholder="Zip"
          disabled={disabled}
          onChange={onChange}
          allowClear={allowClear}
        />
      </Form.Item>
    </Rows>
  );
};

export default Address;
